import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ServiceRow } from '@/lib/database.types';

export function useServices(activiteId: string | null) {
  return useQuery({
    queryKey: ['services', activiteId],
    enabled: !!activiteId,
    queryFn: async (): Promise<ServiceRow[]> => {
      if (!activiteId) return [];
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('activite_id', activiteId)
        .order('nom');
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertServiceInput {
  id?: string;
  activite_id: string;
  code: string;
  nom: string;
  description?: string | null;
  prix_ht: number;
  tva_pct: number;
  unite: string;
  is_active?: boolean;
}

export function useUpsertService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertServiceInput): Promise<ServiceRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('services')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('services').insert(rest).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['services', row.activite_id] });
    },
  });
}

export function useBulkUpdateServiceActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; is_active: boolean }): Promise<void> => {
      const { error } = await supabase
        .from('services')
        .update({ is_active: input.is_active })
        .in('id', input.ids);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['services'] }),
  });
}

export function useBulkDeleteServices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<void> => {
      const { error } = await supabase.from('services').delete().in('id', ids);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['services'] }),
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['services'] });
    },
  });
}
